import { Eye, EyeOff } from 'lucide-react';
import React, { forwardRef, useState } from 'react';
import { FieldError } from 'react-hook-form';

interface FormInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  error?: FieldError | string;
  helperText?: string;
  required?: boolean;
  showPasswordToggle?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      showPasswordToggle,
      prefix,
      suffix,
      className = '',
      type = 'text',
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [internalType, setInternalType] = useState(type);

    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorMessage = typeof error === 'string' ? error : error?.message;
    const hasError = !!errorMessage;

    React.useEffect(() => {
      if (type === 'password' && showPasswordToggle) {
        setInternalType(showPassword ? 'text' : 'password');
      }
    }, [showPassword, type, showPasswordToggle]);

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    return (
      <div className="space-y-1">
        {/* Label */}
        {label && (
          <label htmlFor={inputId} className="block text-meta font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Prefix */}
          {prefix && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500">{prefix}</span>
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            type={internalType}
            id={inputId}
            className={`
              block w-full h-10 px-3 py-2 border border-gray-300 rounded-lg shadow-sm
              text-body-sm text-gray-900 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              ${hasError ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : ''}
              ${prefix ? 'pl-10' : ''}
              ${suffix || showPasswordToggle ? 'pr-10' : ''}
              ${className}
            `}
            aria-invalid={hasError}
            aria-describedby={
              errorMessage ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />

          {/* Suffix / Password Toggle */}
          {(suffix || showPasswordToggle) && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {showPasswordToggle && type === 'password' ? (
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              ) : (
                <span className="text-gray-500">{suffix}</span>
              )}
            </div>
          )}
        </div>

        {/* Helper Text */}
        {helperText && !hasError && (
          <p id={`${inputId}-helper`} className="text-meta text-gray-500">
            {helperText}
          </p>
        )}

        {/* Error Message */}
        {hasError && (
          <p id={`${inputId}-error`} className="text-meta text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export default FormInput;
